import { useCallback, useMemo, useState } from 'react';

const BROWSER_HELP_LINKS: Record<string, string> = {
	chrome: 'https://support.google.com/chrome/answer/142065',
	firefox: 'https://support.mozilla.org/en-US/kb/does-firefox-share-my-location-websites',
	safari: 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac',
	edge: 'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04',
};

const getBrowserName = (): string => {
	const ua = navigator.userAgent.toLowerCase();
	if (ua.includes('edg/')) return 'edge';
	if (ua.includes('chrome') && !ua.includes('edg/')) return 'chrome';
	if (ua.includes('firefox')) return 'firefox';
	if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
	return 'chrome';
};

const STEPS = [
	'Click the lock/site info icon in your browser address bar',
	'Find "Location" in the permissions list',
	'Change it from "Block" to "Allow"',
	'Refresh this page to continue',
];

interface LocationRequiredProps {
	isOpen: boolean;
	onRetrySuccess: () => void;
}

export const LocationRequired: React.FC<LocationRequiredProps> = ({ isOpen, onRetrySuccess }) => {
	const [retrying, setRetrying] = useState(false);
	const [showReload, setShowReload] = useState(false);
	const browserName = useMemo(() => getBrowserName(), []);
	const helpLink = BROWSER_HELP_LINKS[browserName] || BROWSER_HELP_LINKS.chrome;
	const browserLabel = browserName.charAt(0).toUpperCase() + browserName.slice(1);

	const domain = useMemo(() => {
		try {
			const host = window.location.host || '';
			const parts = host.split('.');
			return parts.length >= 2
				? parts.slice(-2).join('.')
				: 'simplici.io';
		} catch {
			return 'simplici.io';
		}
	}, []);

	const handleRetry = useCallback(() => {
		setRetrying(true);
		navigator.geolocation.getCurrentPosition(
			(info) => {
				if (info?.coords) {
					localStorage.setItem('userLocation', JSON.stringify({
						accuracy: info.coords.accuracy,
						altitude: info.coords.altitude,
						altitudeAccuracy: info.coords.altitudeAccuracy,
						latitude: info.coords.latitude,
						longitude: info.coords.longitude,
					}));
					setRetrying(false);
					onRetrySuccess();
				} else {
					setRetrying(false);
				}
			},
			() => {
				setRetrying(false);
				setShowReload(true);
			},
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
		);
	}, [onRetrySuccess]);

	if (!isOpen) return null;

	return (
		<div style={{
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: 'rgba(0, 0, 0, 0.6)',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 999999,
		}}>
			<div style={{
				backgroundColor: 'white',
				borderRadius: '12px',
				padding: '32px',
				maxWidth: '420px',
				width: '90%',
				textAlign: 'center',
			}}>
				<div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
				<h2 style={{ fontSize: '18px', fontWeight: 600, color: '#1a1a1a', marginBottom: '8px' }}>
					Location access is required
				</h2>
				<p style={{ fontSize: '14px', color: '#666', lineHeight: 1.5, marginBottom: '16px' }}>
					For identity verification compliance, we need to verify your location.
					Please enable location sharing for {domain} to continue.
				</p>
				<div style={{ textAlign: 'left', marginBottom: '16px' }}>
					<p style={{ fontSize: '14px', fontWeight: 500, color: '#333', marginBottom: '8px' }}>
						How to enable in {browserLabel}:
					</p>
					<ol style={{ fontSize: '13px', color: '#555', paddingLeft: '20px', lineHeight: 1.6 }}>
						{STEPS.map((step, i) => (
							<li key={i}>{step}</li>
						))}
					</ol>
				</div>
				<a
					href={helpLink}
					target="_blank"
					rel="noopener noreferrer"
					style={{
						display: 'block',
						fontSize: '13px',
						color: '#2563eb',
						textDecoration: 'none',
						marginBottom: '20px',
					}}
				>
					Need more help? View {browserLabel} location settings guide →
				</a>
				<button
					onClick={handleRetry}
					disabled={retrying}
					style={{
						backgroundColor: '#2563eb',
						color: 'white',
						border: 'none',
						borderRadius: '8px',
						padding: '12px 24px',
						fontSize: '15px',
						fontWeight: 500,
						cursor: retrying ? 'not-allowed' : 'pointer',
						opacity: retrying ? 0.7 : 1,
						width: '100%',
					}}
				>
					{retrying ? 'Checking...' : "I've enabled location — Retry"}
				</button>
				{showReload && (
					<p style={{
						fontSize: '13px',
						color: '#666',
						marginTop: '12px',
					}}>
						If you've already enabled location in your browser settings, try reloading the page.
					</p>
				)}
				{showReload && (
					<button
						onClick={() => window.location.reload()}
						style={{
							backgroundColor: 'transparent',
							color: '#2563eb',
							border: '1px solid #2563eb',
							borderRadius: '8px',
							padding: '10px 24px',
							fontSize: '14px',
							fontWeight: 500,
							cursor: 'pointer',
							width: '100%',
							marginTop: '8px',
						}}
					>
						Reload page
					</button>
				)}
			</div>
		</div>
	);
};
